import {useState} from "react";
import {Button} from "../../../basic/button.tsx";
import {Card} from "../../../basic/card.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {CreateProductDialog} from "../../../dialogs/admin/CreateProductDialog";
import {EditProductDialog} from "../../../dialogs/admin/EditProductDialog/index.tsx";
import {PaginationControls} from "../../../shared/Pagination.tsx";
import {SearchInput} from "../../../shared/search/SearchInput.tsx";
import {ProductActions} from "./ProductActions.tsx";
import {ProductTable} from "./ProductTable.tsx";
import {useGetAdminProductsQuery, VwpProduct} from "../../../../store/generated/api.ts";

export function AdminProductsView() {
    const {t} = useTranslationWithKeys();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [editProductDialogOpen, setEditProductDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<VwpProduct | null>(null);
    const [createProductDialogOpen, setCreateProductDialogOpen] = useState(false);

    const {
        data: productsData,
        isLoading,
        error,
        refetch
    } = useGetAdminProductsQuery({
        page: currentPage,
        size: pageSize,
        search: searchTerm || undefined
    });

    const products = productsData?.products || [];
    const pagination = productsData?.pagination;

    const handleProductClick = (product: VwpProduct) => {
        setSelectedProduct(product);
        setEditProductDialogOpen(true);
    };

    const handleCreateProduct = () => {
        setCreateProductDialogOpen(true);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page - 1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(0);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('admin.products.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('admin.products.description')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-64">
                            <p>{t('common.loading')}</p>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('admin.products.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('admin.products.description')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-64">
                            <div className="text-center">
                                <p className="text-red-500">{t('common.error')}</p>
                                <Button
                                    variant="outline"
                                    onClick={() => refetch()}
                                    className="mt-2"
                                >
                                    {t('common.retry')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1>{t('admin.products.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('admin.products.description')}</p>
            </div>

            <Card className="shadow-sm border-border/50">
                <div className="p-6">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <SearchInput
                                placeholder={t('admin.products.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            <ProductActions
                                onCreateProduct={handleCreateProduct}
                            />
                        </div>
                        <ProductTable
                            products={products}
                            onProductClick={handleProductClick}
                        />

                        {pagination && (
                            <PaginationControls
                                currentPage={pagination.page + 1}
                                totalPages={pagination.totalPages}
                                pageSize={pagination.size}
                                pageSizeOptions={[10, 25, 50, 100]}
                                paginationInfo={{
                                    start: pagination.page * pagination.size + 1,
                                    end: Math.min((pagination.page + 1) * pagination.size, pagination.total),
                                    total: pagination.total
                                }}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                                rowsPerPageLabel={t('common.rowsPerPage')}
                                previousLabel={t('common.previous')}
                                nextLabel={t('common.next')}
                            />
                        )}
                    </div>
                </div>
            </Card>

            <EditProductDialog
                open={editProductDialogOpen}
                onOpenChange={(open) => {
                    setEditProductDialogOpen(open);
                    if (!open) {
                        setSelectedProduct(null);
                    }
                }}
                selectedProduct={selectedProduct}
            />

            <CreateProductDialog
                open={createProductDialogOpen}
                onOpenChange={setCreateProductDialogOpen}
            />
        </div>
    );
}